import React from 'react';
import { Box } from '@chakra-ui/react';
import { DayPicker, type DayPickerProps } from '@daypicker/react';
import { CaretDown, CaretLeft, CaretRight } from 'phosphor-react';

const CELL = '34px';

const CalendarChevron = ({
  className,
  orientation,
}: {
  className?: string;
  orientation?: 'left' | 'right' | 'up' | 'down';
}) => {
  if (orientation === 'left') return <CaretLeft size={14} className={className} />;
  if (orientation === 'right') return <CaretRight size={14} className={className} />;
  return <CaretDown size={14} className={className} />;
};

export const Calendar = ({ showOutsideDays = true, components, ...props }: DayPickerProps) => (
  <Box
    sx={{
      '.rdp-root': {
        width: 'fit-content',
        fontSize: '13px',
        color: 'content.strong',
      },
      '.rdp-months': { position: 'relative', display: 'flex', flexDirection: 'column', gap: '8px' },
      '.rdp-month': { width: '100%' },
      '.rdp-month_caption': {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: CELL,
        margin: `0 ${CELL}`,
        marginBottom: '4px',
        zIndex: 2,
      },
      '.rdp-caption_label': {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '100%',
        fontSize: '13px',
        fontWeight: 500,
      },
      '.rdp-dropdowns': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        width: '100%',
        height: CELL,
      },
      '.rdp-dropdown_root': {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        height: '30px',
        px: '10px',
        border: '1px solid',
        borderColor: 'line.input',
        borderRadius: '8px',
        bg: 'surface.base',
        boxShadow: 'control',
        _hover: { borderColor: 'line.ring' },
      },
      '.rdp-dropdown_root:has(:focus-visible)': {
        borderColor: 'line.ring',
        boxShadow: 'outline',
      },
      '.rdp-dropdown': {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        opacity: 0,
        cursor: 'pointer',
      },
      '.rdp-nav': {
        position: 'absolute',
        top: 0,
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        zIndex: 1,
      },
      '.rdp-button_previous, .rdp-button_next': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: CELL,
        height: CELL,
        borderRadius: '8px',
        color: 'content.subtle',
        _hover: { bg: 'surface.selected', color: 'content.strong' },
        _disabled: { opacity: 0.4, pointerEvents: 'none' },
      },
      '.rdp-weekday': {
        width: CELL,
        height: CELL,
        fontSize: '11px',
        fontWeight: 500,
        color: 'content.faint',
        textTransform: 'lowercase',
      },
      '.rdp-day': { width: CELL, height: CELL, padding: 0, textAlign: 'center' },
      '.rdp-day_button': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: CELL,
        height: CELL,
        borderRadius: '8px',
        fontSize: '13px',
        color: 'content.strong',
        cursor: 'pointer',
        position: 'relative',
      },
      '.rdp-day:not([data-selected]) .rdp-day_button:hover': { bg: 'surface.selected' },
      '.rdp-day_button:focus-visible': { boxShadow: 'outline', outline: 'none' },
      '.rdp-outside .rdp-day_button': { color: 'content.faint' },
      '.rdp-disabled .rdp-day_button': {
        color: 'content.faint',
        textDecoration: 'line-through',
        pointerEvents: 'none',
      },
      '[data-selected] .rdp-day_button': {
        bg: 'state.link',
        color: 'content.inverse',
        fontWeight: 500,
      },
      '.rdp-today .rdp-day_button::after': {
        content: '""',
        position: 'absolute',
        bottom: '4px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '3px',
        height: '3px',
        borderRadius: 'full',
        bg: 'state.link',
      },
      '.rdp-today[data-selected] .rdp-day_button::after': { bg: 'content.inverse' },
      '.rdp-range_middle .rdp-day_button': {
        bg: 'surface.selected',
        color: 'content.strong',
        borderRadius: 0,
        fontWeight: 400,
      },
      '.rdp-range_start:not(.rdp-range_end) .rdp-day_button': {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
      '.rdp-range_end:not(.rdp-range_start) .rdp-day_button': {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      },
      '.rdp-range_start .rdp-day_button, .rdp-range_end .rdp-day_button': {
        bg: 'state.link',
        color: 'content.inverse',
        fontWeight: 500,
      },
      '.rdp-hidden': { visibility: 'hidden' },
    }}
  >
    <DayPicker
      showOutsideDays={showOutsideDays}
      components={{ Chevron: CalendarChevron, ...components }}
      formatters={{
        formatMonthDropdown: (date: Date) => date.toLocaleString('pt-BR', { month: 'short' }),
      }}
      {...props}
    />
  </Box>
);
